import React from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import classNames from 'classnames';
import get from 'lodash.get';

import { package as PackagePropType } from 'lib/CustomPropTypes';
import { formatDuration, timeFrom, getFormattedDateString } from 'lib/DateTime';
import { IMAGE_SIZES } from 'lib/imageUtils';
import { isVideoLive } from 'lib/videoUtils';

import ButtonHoverAnimation from 'components/ButtonHoverAnimation';
import Link from 'components/Link';
import TypeIcon from 'components/TypeIcon';
import TeasePicture from 'components/TeasePicture';
import Unibrow from 'components/Unibrow';

import TitleLogo from 'components/TitleLogo';
import config from './config';

const stringCheck = (text, defaultValue) => ((typeof text === 'string' && text.trim()) ? text : defaultValue);


class Pancake extends React.Component {
  static propTypes = {
    content: PackagePropType.isRequired,
    vertical: PropTypes.string.isRequired,
    railAdjacent: PropTypes.bool,
    pkgClassName: PropTypes.string,
  };

  static defaultProps = {
    railAdjacent: false,
    pkgClassName: 'pkg pancake',
  }

  getItem = (i) => {
    const { content: { items } } = this.props;
    return items[i];
  }

  getKey = (i) => {
    if (this.getItem(i)) {
      return this.getItem(i).id;
    }
    return `item-${i}`;
  }

  getLayout = (artVariant = false) => {
    const { railAdjacent } = this.props;
    if (artVariant && artVariant !== false) {
      return `${railAdjacent ? 'rail' : 'full'}${artVariant}`;
    }
    return railAdjacent ? 'rail' : 'full';
  }

  getLength = () => {
    const { content: { items } } = this.props;
    return items.length;
  }

  getConfig = (key, i = false, artVariant = false) => {
    const lay = this.getLayout(artVariant);
    const len = this.getLength();
    if (config[key] && config[key][len] && config[key][len][lay]) {
      if (i !== false) {
        return config[key][len][lay][i] || false;
      }
      return config[key][len][lay];
    }
    return false;
  }

  getImageSizes = (i) => {
    const images = this.getConfig('images', i) || [];
    return images.reduce((accumulator, flavor, idx) => {
      accumulator[IMAGE_SIZES[idx]] = flavor;
      return accumulator;
    }, {});
  }

  getPicture = (i) => {
    if (!this.hasMetaFlag('showTeaseImage', i, true)) {
      return false;
    }
    const item = this.getItem(i);
    const sizes = this.getImageSizes(i);
    if (Object.keys(sizes).length >= 1 && item.computedValues && item.computedValues.teaseImage) {
      return (
        <TeasePicture
          responsiveFlavors={sizes}
          className="pancake__tease-picture"
          type={item.type}
          computedValues={item.computedValues}
          placeholder={false}
          retina
        />
      );
    }
    return false;
  }

  getFloatClasses = (i) => {
    const float = this.getConfig('float', i);
    if (float && float.length) {
      return float.map((val, key) => {
        if (typeof val === 'number') {
          const cls = `pk-f${val ? 'y' : 'n'}`;
          if (key > 0) {
            return `${cls}-${IMAGE_SIZES[key]}`;
          }
          return cls;
        }
        return null;
      })
        .filter((v) => v)
        .join(' ');
    }
    return null;
  }

  getHeadlineClasses = (i) => {
    const variant = this.hasMetaFlag('showTeaseImage', i, true) ? false : 'NoArt';
    const fonts = this.getConfig('headlineFonts', i, variant);
    const ret = [
      this.isSerif() ? 'fw1 f3 lh-copy' : 'f5 lh-none',
    ];
    if (fonts && fonts.length) {
      // Convert integers to toolkit classes.
      let f = 0;
      while (f < fonts.length) {
        if (fonts[f]) {
          const s = parseInt(fonts[f], 10) - (this.isSerif() ? 2 : 0);
          ret.push(`f${s}${f > 0 ? `-${IMAGE_SIZES[f]}` : ''}`);
        }
        f += 1;
      }
    }
    return ret;
  }

  getTease = (i) => {
    const item = this.getItem(i);
    const isVideo = item && item.type && item.type === 'video';
    const picture = this.getPicture(i);

    return item && (
      <div className={classNames('pancake__tease', this.getFloatClasses(i))} data-contentid={item.id}>
        {!!picture && (
          <Link
            to={item.computedValues.url}
            className="pancake__tease-picture-link"
          >
            {picture}
            <TypeIcon type={item.type} className="pancake__typeIcon" />
          </Link>
        )}
        <div className="pancake__info">
          {this.unibrow(item)}
          {this.headline(item, i)}
          {isVideo && !picture && this.getDuration(i)}
          {!isVideo && this.getTimestamp(i)}
        </div>
      </div>
    );
  }

  getDuration = (i) => {
    const item = this.getItem(i);
    const { url } = item.computedValues;
    const isLive = isVideoLive(item.item);
    const durationLabel = isLive ? i18next.t('LIVE') : formatDuration(item.item.duration);
    return (
      <Link
        to={url}
        className={classNames('pancake__duration', { 'pancake__duration--is-live': isLive }, 'mt2')}
      >
        {durationLabel && (
        <span className="founders-mono f2 mh3">
          {durationLabel}
        </span>
        )}
        <TypeIcon type={item.type} className="pancake__typeIcon" />
      </Link>
    );
  }

  getTimestamp = (i) => {
    if (this.hasMetaFlag('showTimestamp', i, false)) {
      const { datePublished } = this.getItem(i).item;
      if (!datePublished) {
        return null;
      }
      const date = new Date(datePublished);
      const sixtyDaysAgo = (new Date()).getTime() - (60 * 86400 * 1000);
      const publishedMoreThanSixtyDaysAgo = date.getTime() < sixtyDaysAgo;
      let ts = null;
      if (publishedMoreThanSixtyDaysAgo) {
        ts = getFormattedDateString(datePublished);
      } else {
        ts = `${i18next.t('time_ago', { time: timeFrom(datePublished) })}`;
      }
      return (
        <div className="founders-mono gray-80 f2 mt1 ttc">{ts}</div>
      );
    }
    return null;
  }

  isSerif = () => {
    const { vertical } = this.props;
    return ['today', 'knowyourvalue'].includes(vertical);
  }

  hasMetaFlag = (flag, i, defaultVal = true) => {
    const { content: { items, metadata: { [flag]: pkgFlag } } } = this.props;
    if (typeof pkgFlag !== 'undefined') {
      return pkgFlag;
    }
    const { metadata: { [flag]: teaseFlag } } = items[i] || {};
    if (typeof teaseFlag !== 'undefined') {
      return teaseFlag;
    }
    return defaultVal;
  }

  unibrow = (item) => {
    const { unibrow } = item.computedValues || {};
    if (!unibrow) {
      return null;
    }

    const taxonomy = get(item, 'item.taxonomy');
    // taxonomy and primaryVertical are nullable properties from API
    const vertical = (taxonomy && taxonomy.primaryVertical && taxonomy.primaryVertical.name) || '';

    return (
      <Unibrow
        unibrow={unibrow}
        className={classNames(
          'pancake__unibrow',
          { 'pancake__unibrow--think': vertical.toLowerCase() === 'think' },
          'mt0 mb2 founders-mono fw1 f2 lh-none ttu ls-tight',
        )}
      />
    );
  }

  headline = (item, i) => {
    const { headline, url } = item.computedValues || {};
    return (
      <h3
        className={classNames(
          'pancake__headline mv0',
          this.getHeadlineClasses(i),
        )}
      >
        <Link to={url}>
          {headline}
        </Link>
      </h3>
    );
  }

  getTitleOrLogoComponent = (logoUrl, title, titleUrl) => {
    let Component = null;
    if (logoUrl || title) {
      Component = <TitleLogo logoUrl={logoUrl} title={title} />;
      if (titleUrl) {
        Component = (
          <Link to={titleUrl}>
            {Component}
          </Link>
        );
      }
    }
    return Component;
  }

  getTitleOrLogo = () => {
    const {
      content: {
        metadata: {
          title,
          titleUrl,
          logoUrl,
        } = {},
      },
    } = this.props;

    const titleOrLogo = this.getTitleOrLogoComponent(logoUrl, title, titleUrl);

    return titleOrLogo && (
      <h2 className="pancake__title">
        {titleOrLogo}
      </h2>
    );
  }

  // TODO: Refactor to use KeyUpdates component (ML)
  // Current KeyUpdates is absolutely positioned and styled uniquely
  keyUpdates = () => {
    const { vertical } = this.props;
    const {
      content: {
        metadata: {
          sectionTitle,
          listItems,
          updatesText,
          updatesUrl,
        } = {},
      },
    } = this.props;

    const defaultTitle = i18next.t(config.defaultTitle[vertical])
    || i18next.t(config.defaultTitle.default);

    return (
      <div className="mb8 mb0-m">
        <h3 className="pancake__title">
          {stringCheck(sectionTitle, defaultTitle)}
        </h3>
        <ul className={classNames('pancake__list', 'publico-txt f3 lh-copy')}>
          {!!listItems && (listItems.split(/\r?\n/) || [])
            .filter((line) => line)
            .map((item, i) => (
              <li className="pancake__list-item" key={this.getKey(i)}>{item}</li>
            ))}
        </ul>
        {stringCheck(updatesUrl, false) && (
          <div className="pancake__buttonWrapper">
            <ButtonHoverAnimation
              size="normal"
              type="link"
              url={updatesUrl}
              title={stringCheck(updatesText, i18next.t('See All'))}
            />
          </div>
        )}
      </div>
    );
  }

  layout2 = () => {
    const { railAdjacent } = this.props;

    const wrapContent = ['layout-grid-item', 'grid-col-12', 'grid-col-6-m'];
    const wrapRail = ['layout-grid-item', 'grid-col-12', 'grid-col-6-m'];

    const itemMain0 = ['layout-subgrid-item', 'grid-col-12', 'grid-col-6-m'];
    const itemMain1 = ['layout-subgrid-item', 'grid-col-12', 'grid-col-6-m'];

    if (railAdjacent) {
      itemMain0.push('grid-col-12-l', 'grid-col-6-xl');
      itemMain1.push('grid-col-12-l', 'grid-col-6-xl');
    } else {
      wrapContent.push('grid-col-8-l', 'grid-col-9-xl');
      wrapRail.push('grid-col-4-l', 'grid-col-3-xl');

      itemMain0.push('grid-col-7-5-l grid-col-8-xl');
      itemMain1.push('grid-col-4-5-l grid-col-4-xl');
    }

    return (
      <>
        <div className={classNames('pancake__content-column', wrapContent)}>
          <div className="layout-subgrid-container">
            <div className="layout-subgrid-item grid-col-12">
              {this.getTitleOrLogo()}
            </div>
            <div className="layout-subgrid-item grid-col-12">
              <div className="layout-subgrid-container">
                <div className={classNames('pancake-item-0', 'pancake-M', itemMain0)}>
                  {this.getTease(0)}
                </div>
                <div className={classNames('pancake-item-1', 'pancake-S', itemMain1)}>
                  {this.getTease(1)}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className={classNames('pancake__key-updates', wrapRail)}>
          {this.keyUpdates()}
        </div>
      </>
    );
  }

  layout3 = () => {
    const { railAdjacent } = this.props;

    const wrapContent = ['layout-grid-item', 'grid-col-12', 'grid-col-6-m'];
    const wrapRail = ['layout-grid-item', 'grid-col-12', 'grid-col-6-m'];

    const wrapItemMain = ['layout-subgrid-item', 'grid-col-12', 'grid-col-12-m'];
    const wrapItemSub = ['layout-subgrid-item', 'grid-col-12', 'grid-col-12-m'];

    const itemMain = ['layout-subgrid-item', 'grid-col-12'];
    const itemSub = ['layout-subgrid-item', 'grid-col-12'];

    if (railAdjacent) {
      itemSub.push('grid-col-6-xl');
    } else {
      wrapContent.push('grid-col-8-l', 'grid-col-9-xl');
      wrapRail.push('grid-col-4-l', 'grid-col-3-xl');

      wrapItemMain.push('grid-col-7-5-l', 'grid-col-8-xl');
      wrapItemSub.push('grid-col-4-5-l', 'grid-col-4-xl');

      itemMain.push('grid-col-12-l');
      itemSub.push('grid-col-12-xl');
    }

    return (
      <>
        <div className={classNames('pancake__content-column', wrapContent)}>
          <div className="layout-subgrid-container">
            <div className="layout-subgrid-item grid-col-12">
              {this.getTitleOrLogo()}
            </div>
            <div className={classNames(wrapItemMain)}>
              <div className="layout-subgrid-container">
                {[0, 1].map((i) => (
                  <div key={this.getKey(i)} className={classNames(`pancake-item-${i}`, 'pancake-M', itemMain, { 'dn db-l dn-xl': (i === 1) })}>
                    {this.getTease(i)}
                  </div>
                ))}
              </div>
            </div>
            <div className={classNames(wrapItemSub)}>
              <div className="layout-subgrid-container">
                {[1, 2].map((i) => (
                  <div key={this.getKey(i)} className={classNames(`pancake-item-${i}`, 'pancake-S', itemSub, { 'dn-l db-xl': (i === 1) })}>
                    {this.getTease(i)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className={classNames('pancake__key-updates', wrapRail)}>
          {this.keyUpdates()}
        </div>
      </>
    );
  }

  layout4 = () => {
    const { railAdjacent } = this.props;

    const wrapContent = ['layout-grid-item', 'grid-col-12', 'grid-col-6-m'];
    const wrapRail = ['layout-grid-item', 'grid-col-12', 'grid-col-6-m'];

    const wrapItemMain = ['layout-subgrid-item', 'grid-col-12-m'];
    const wrapItemSub = ['layout-subgrid-item', 'grid-col-12-m'];

    const itemMain = [
      ['layout-subgrid-item', 'grid-col-12', 'grid-col-6-m'],
      ['layout-subgrid-item', 'grid-col-12', 'grid-col-6-m'],
    ];
    const itemSub = [
      ['layout-subgrid-item', 'grid-col-12'],
      ['layout-subgrid-item', 'grid-col-12'],
    ];

    if (railAdjacent) {
      itemMain[0].push('grid-col-12-l', 'grid-col-6-xl');
      itemMain[1].push('grid-col-12-l', 'grid-col-6-xl');

      itemSub[0].push('grid-col-6-xl');
      itemSub[1].push('grid-col-6-xl');
    } else {
      wrapContent.push('grid-col-8-l', 'grid-col-9-xl');
      wrapRail.push('grid-col-4-l', 'grid-col-3-xl');

      wrapItemMain.push('grid-col-12-l', 'grid-col-8-xl');
      wrapItemSub.push('grid-col-12-l', 'grid-col-4-xl');

      itemMain[0].push('grid-col-7-5-l', 'grid-col-6-xl');
      itemMain[1].push('grid-col-4-5-l', 'grid-col-6-xl');

      itemSub[0].push('grid-col-7-5-l', 'grid-col-12-xl');
      itemSub[1].push('grid-col-4-5-l', 'grid-col-12-xl');
    }

    return (
      <>
        <div className={classNames('pancake__content-column', wrapContent)}>
          <div className="layout-subgrid-container">
            <div className="layout-subgrid-item grid-col-12">
              {this.getTitleOrLogo()}
            </div>
            <div className={classNames(wrapItemMain)}>
              <div className="layout-subgrid-container">
                {[0, 1].map((i, k) => (
                  <div key={this.getKey(i)} className={classNames(`pancake-item-${i}`, 'pancake-M', itemMain[k])}>
                    {this.getTease(i)}
                  </div>
                ))}
              </div>
            </div>
            <div className={classNames(wrapItemSub)}>
              <div className="layout-subgrid-container">
                {[2, 3].map((i, k) => (
                  <div key={this.getKey(i)} className={classNames(`pancake-item-${i}`, 'pancake-S', itemSub[k])}>
                    {this.getTease(i)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className={classNames('pancake__key-updates', wrapRail)}>
          {this.keyUpdates()}
        </div>
      </>
    );
  }

  layout5 = () => {
    const { railAdjacent } = this.props;

    const itemClassM = ['layout-subgrid-item', 'grid-col-12', 'grid-col-6-m'];
    const wrapClassS = ['layout-subgrid-item', 'grid-col-12'];
    const itemClassS = ['layout-subgrid-item', 'grid-col-12', 'grid-col-6-m'];

    if (railAdjacent) {
      itemClassM.push('grid-col-12-l', 'grid-col-6-xl');
      wrapClassS.push('dn-l', 'db-xl');
      itemClassS.push('grid-col-6-xl');
    } else {
      itemClassM.push('grid-col-4-xl');
      wrapClassS.push('grid-col-4-xl');
      itemClassS.push('grid-col-12-xl');
    }

    return (
      <>
        <div className="layout-grid-item grid-col-12">
          {this.getTitleOrLogo()}
        </div>
        <div className={classNames('pancake-item-0', 'layout-grid-item grid-col-12 grid-col-6-m')}>
          {this.getTease(0)}
        </div>
        <div className="layout-grid-item grid-col-12 grid-col-6-m">
          <div className="layout-subgrid-container">
            {[1, 2].map((i) => (
              <div key={this.getKey(i)} className={classNames(`pancake-item-${i}`, 'pancake-M', itemClassM)}>
                {this.getTease(i)}
              </div>
            ))}
            <div className={classNames(wrapClassS)}>
              <div className="layout-subgrid-container">
                {[3, 4].map((i) => (
                  <div key={this.getKey(i)} className={classNames(`pancake-item-${i}`, 'pancake-S', itemClassS)}>
                    {this.getTease(i)}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        {!!railAdjacent && (
          <>
            {[3, 4].map((i) => (
              <div key={this.getKey(i)} className={classNames('pancake-S', 'layout-grid-item grid-col-6-l dn db-l dn-xl')}>
                {this.getTease(i)}
              </div>
            ))}
          </>
        )}
      </>
    );
  }

  layout6 = () => {
    const { railAdjacent } = this.props;

    const itemClassM = ['layout-grid-item', 'grid-col-12', 'grid-col-6-m', 'grid-col-6-l'];
    const wrapClassS = ['layout-grid-item', 'grid-col-12'];
    const itemClassS = ['layout-subgrid-item', 'grid-col-12', 'grid-col-3-m'];

    if (railAdjacent) {
      itemClassS.push('grid-col-6-l', 'grid-col-3-xl');
    } else {
      itemClassM.push('grid-col-2-xl');
      wrapClassS.push('grid-col-4-xl');
      itemClassS.push('grid-col-3-l', 'grid-col-6-xl');
    }

    return (
      <>
        <div className="layout-grid-item grid-col-12">
          {this.getTitleOrLogo()}
        </div>
        <div className="pancake-item-0 layout-grid-item grid-col-12 grid-col-6-m">
          {this.getTease(0)}
        </div>
        <div className={classNames('pancake-item-1', 'pancake-M', itemClassM)}>
          {this.getTease(1)}
        </div>
        <div className={classNames(wrapClassS)}>
          <div className="layout-subgrid-container">
            {[2, 3, 4, 5].map((i) => (
              <div key={this.getKey(i)} className={classNames(`pancake-item-${i}`, 'pancake-S', itemClassS)}>
                {this.getTease(i)}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  layout7 = () => {
    const { railAdjacent } = this.props;

    const itemClassL = ['layout-grid-item', 'grid-col-12', 'grid-col-6-m'];
    const wrapClassM = ['layout-grid-item', 'grid-col-12', 'grid-col-6-m'];
    const itemClassM = ['layout-subgrid-item', 'grid-col-12'];
    const wrapClassS = ['layout-grid-item', 'grid-col-12'];
    const itemClassS = ['layout-subgrid-item', 'grid-col-12', 'grid-col-3-m'];

    if (railAdjacent) {
      itemClassS.push('grid-col-6-l', 'grid-col-3-xl');
    } else {
      itemClassL.push('grid-col-4-xl');
      wrapClassM.push('grid-col-4-xl');
      itemClassM.push('grid-col-6-xl');
      wrapClassS.push('grid-col-4-xl');
      itemClassS.push('grid-col-3-l', 'grid-col-6-xl');
    }

    return (
      <>
        <div className="layout-grid-item grid-col-12">
          {this.getTitleOrLogo()}
        </div>
        <div className={classNames('pancake-item-0', itemClassL)}>
          {this.getTease(0)}
        </div>
        <div className={classNames(wrapClassM)}>
          <div className="layout-subgrid-container">
            {[1, 2].map((i) => (
              <div key={this.getKey(i)} className={classNames(`pancake-item-${i}`, 'pancake-M', itemClassM)}>
                {this.getTease(i)}
              </div>
            ))}
          </div>
        </div>
        <div className={classNames(wrapClassS)}>
          <div className="layout-subgrid-container">
            {[3, 4, 5, 6].map((i) => (
              <div key={this.getKey(i)} className={classNames(`pancake-item-${i}`, 'pancake-S', itemClassS)}>
                {this.getTease(i)}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  layout8 = () => {
    const { railAdjacent } = this.props;

    const wrapClassM = ['layout-grid-item', 'grid-col-12', 'grid-col-6-m'];
    const itemClassM = ['layout-subgrid-item', 'grid-col-12'];
    const itemClassS = ['layout-subgrid-item', 'grid-col-12', 'grid-col-6-l'];

    if (railAdjacent) {
      wrapClassM.push('grid-col-12-l', 'grid-col-6-xl');
      itemClassM.push('grid-col-6-l');
    } else {
      wrapClassM.push('grid-col-6-l', 'grid-col-6-xl');
      itemClassM.push('grid-col-12-l');
      itemClassS.push('grid-col-4-xl');
    }

    return (
      <>
        <div className="layout-grid-item grid-col-12">
          {this.getTitleOrLogo()}
        </div>
        <div className={classNames(wrapClassM)}>
          <div className="layout-subgrid-container">
            {[0, 1].map((i) => {
              if (railAdjacent) {
                return (
                  <div key={this.getKey(i)} className={classNames(`pancake-item-${i}`, 'pancake-M', itemClassM, 'grid-col-12-xl')}>
                    {this.getTease(i)}
                  </div>
                );
              }
              return (
                <div key={this.getKey(i)} className={classNames(`pancake-item-${i}`, 'pancake-M', itemClassM, `grid-col-${!i ? 8 : 4}-xl`)}>
                  {this.getTease(i)}
                </div>
              );
            })}
          </div>
        </div>
        <div className={classNames(wrapClassM)}>
          <div className="layout-subgrid-container">
            {[2, 3, 4, 5, 6, 7].map((i) => (
              <div key={this.getKey(i)} className={classNames(`pancake-item-${i}`, 'pancake-S', itemClassS)}>
                {this.getTease(i)}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  render() {
    const {
      content: {
        id,
        items = [],
      },
      railAdjacent,
      pkgClassName,
    } = this.props;

    const { length } = items;

    const containerClasses = classNames(
      pkgClassName,
      'pancake',
      `pancake-items-${length}`,
      {
        railAdjacent,
        fullWidth: !railAdjacent,
      },
      'layout-grid-container gutter-collapse',
    );

    const layoutFunc = this[`layout${length}`];

    return !items.includes(null) && (
      <section className={containerClasses} data-packageid={id}>
        {!!layoutFunc && layoutFunc(items)}
      </section>
    );
  }
}

export default Pancake;
